'use client';

import { ComponentPropsWithoutRef, useEffect, useRef } from 'react';
import { useInView, useMotionValue, useSpring } from 'motion/react';
import { cn } from '@/lib/utils';

interface NumberTickerProps extends ComponentPropsWithoutRef<'span'> {
  value: number;
  /** "up" counts to value, "down" counts from value. */
  direction?: 'up' | 'down';
  /** Animation delay in seconds. */
  delay?: number;
  /** Decimal places. */
  decimalPlaces?: number;
  /** Spring stiffness — higher = snappier. */
  stiffness?: number;
}

/**
 * Magic UI–style number ticker. Animates to `value` when scrolled into view
 * using a Motion spring for buttery deceleration.
 */
export function NumberTicker({
  value,
  direction = 'up',
  delay = 0,
  decimalPlaces = 0,
  stiffness = 80,
  className,
  ...props
}: NumberTickerProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const motionValue = useMotionValue(direction === 'down' ? value : 0);
  const springValue = useSpring(motionValue, { damping: 30, stiffness });
  const isInView = useInView(ref, { once: true, margin: '0px' });

  useEffect(() => {
    if (!isInView) return;
    const t = window.setTimeout(() => {
      motionValue.set(direction === 'down' ? 0 : value);
    }, delay * 1000);
    return () => window.clearTimeout(t);
  }, [motionValue, isInView, delay, value, direction]);

  useEffect(() => {
    return springValue.on('change', (latest) => {
      if (!ref.current) return;
      ref.current.textContent = Intl.NumberFormat('en-US', {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
      }).format(Number(latest.toFixed(decimalPlaces)));
    });
  }, [springValue, decimalPlaces]);

  return (
    <span {...props} ref={ref} className={cn('inline-block tabular-nums', className)}>
      {direction === 'down' ? value : 0}
    </span>
  );
}
